import { useLocation, useParams } from "react-router-dom";
import { MapPin, Clock, Phone, Mail, ArrowLeft, Edit, User, Calendar } from "lucide-react";
import { ImagePlus } from 'lucide-react'; // Add this import
import { useNavigate } from "react-router-dom";
import "./MosquePage.scss";
import { useState, useEffect, useRef } from "react";
import MosqueLoader from "../../utils/MosqueLoader";
import { publicRequest } from "../../requestMethods";
import Navbar from "../Navbar/Navbar";
import { Navigation } from 'lucide-react';  // Add Navigation icon
import { Link } from "react-router-dom";
import { Image, Trash2, AlertTriangle, Flag } from 'lucide-react';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { getDishDisplayName } from '../../utils/dishDisplay';
import { DEFAULT_AVATAR } from '../../utils/constants';

const MosquePage = () => {
    const { mosqueID } = useParams();
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const [mosque, setMosque] = useState({ images: [] });
    const [loading, setLoading] = useState(true);
    const [user] = useState(JSON.parse(localStorage.getItem('user')));
    // console.log("user", user);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const location = useLocation();
    const commentsRef = useRef(null);
    const [visitStats, setVisitStats] = useState(null);
    const visitRecorded = useRef(false);
    const [showImageUpload, setShowImageUpload] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [imageLoading, setImageLoading] = useState(true);
    const [uploadError, setUploadError] = useState(null);
    const [deletingIndex, setDeletingIndex] = useState(null);
    const [deleteConfirmIndex, setDeleteConfirmIndex] = useState(null);
    const [deleteConfirmCallback, setDeleteConfirmCallback] = useState(null);
    const [commentDeleteConfirmId, setCommentDeleteConfirmId] = useState(null);
    const [deletingCommentId, setDeletingCommentId] = useState(null);
    const [showReportModal, setShowReportModal] = useState(false);
    const [reportCategory, setReportCategory] = useState('');
    const [reportDescription, setReportDescription] = useState('');
    const [reportContact, setReportContact] = useState('');
    const [reportSubmitting, setReportSubmitting] = useState(false);
    const [reportSuccess, setReportSuccess] = useState(false);

    useEffect(() => {
        if (deleteConfirmIndex !== null || commentDeleteConfirmId !== null || showReportModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [deleteConfirmIndex, commentDeleteConfirmId, showReportModal]);

    const isOwnImage = (image) => {
        if (!user || !image?.uploadedBy) return false;
        return (
            (image.uploadedBy.email && image.uploadedBy.email === user.email) ||
            (image.uploadedBy.googleId && (image.uploadedBy.googleId === user.googleId || image.uploadedBy.googleId === user.sub))
        );
    };

    const handleDeleteImage = (index, e, onSuccess) => {
        e?.stopPropagation();
        if (!user) return;
        setDeleteConfirmIndex(index);
        setDeleteConfirmCallback(onSuccess || null);
    };

    const confirmDeleteImage = async () => {
        const index = deleteConfirmIndex;
        const callback = deleteConfirmCallback;
        if (index === null || index === undefined) return;
        setDeleteConfirmIndex(null);
        setDeleteConfirmCallback(null);
        setDeletingIndex(index);
        setUploadError(null);
        try {
            await publicRequest.delete(`/mosques/delete-image/${mosqueID}`, {
                data: {
                    imageIndex: index,
                    user: { email: user.email, googleId: user.googleId || user.sub }
                }
            });
            setMosque(prev => ({
                ...prev,
                images: prev.images.filter((_, i) => i !== index)
            }));
            setSelectedImage(null);
            callback?.();
        } catch (err) {
            snackbar.error(err.response?.data?.message || 'Failed to delete image');
        } finally {
            setDeletingIndex(null);
        }
    };

    const isOwnComment = (comment) => {
        if (!user || !comment) return false;
        return (
            (comment.email && comment.email === user.email) ||
            (comment.googleId && (comment.googleId === user.googleId || comment.googleId === user.sub))
        );
    };

    const handleDeleteComment = (commentId, e) => {
        e?.stopPropagation();
        if (!user) return;
        setCommentDeleteConfirmId(commentId);
    };

    const confirmDeleteComment = async () => {
        const commentId = commentDeleteConfirmId;
        if (!commentId) return;
        setCommentDeleteConfirmId(null);
        setDeletingCommentId(commentId);
        setUploadError(null);
        try {
            await publicRequest.delete(`/mosques/delete-comment/${mosqueID}`, {
                data: {
                    commentId,
                    user: { email: user.email, googleId: user.googleId || user.sub }
                }
            });
            setMosque(prev => ({
                ...prev,
                comments: prev.comments.filter(c => c._id !== commentId)
            }));
        } catch (err) {
            snackbar.error(err.response?.data?.message || 'Failed to delete comment');
        } finally {
            setDeletingCommentId(null);
        }
    };

    useEffect(() => {
        // Check if URL has #comments hash
        if (location.hash === '#comments' && commentsRef.current) {
            // Scroll to comments section with smooth behavior
            commentsRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    }, [location, commentsRef]);

    useEffect(() => {
        const fetchDataAndRecordVisit = async () => {
            try {
                // Fetch mosque data
                const response = await publicRequest(`/mosques/get-mosque/${mosqueID}`);
                setMosque(response.data);

                // Only record visit and fetch stats if we haven't already
                if (!visitRecorded.current) {
                    visitRecorded.current = true; // Set flag before API call

                    // Record the visit
                    await publicRequest.post(`/mosques/record-visit/${mosqueID}`, {
                        user: user ? {
                            googleId: user.googleId,
                            name: user.name,
                            email: user.email,
                            picture: user.picture
                        } : null
                    });

                    // Fetch visit statistics
                    const statsResponse = await publicRequest.get(`/mosques/visits/${mosqueID}`);
                    setVisitStats(statsResponse.data);
                }
            } catch (error) {
                console.error("Error:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchDataAndRecordVisit();
    }, [mosqueID]);

    const handleCommentSubmit = async (e) => {
        e.preventDefault();
        if (!newComment.trim() || !user) return;

        try {
            setLoading(true);
            const response = await publicRequest.post(`/mosques/add-comment/${mosqueID}`, {
                content: newComment,
                user: {
                    name: user.name,
                    email: user.email,
                    picture: user.picture,
                    googleId: user.sub
                }
            });

            setMosque(prev => ({
                ...prev,
                comments: [response.data.comments[response.data.comments.length - 1], ...prev.comments]
            }));
            setNewComment('');
        } catch (error) {
            console.error('Error posting comment:', error);
        } finally {
            setLoading(false);
        }
    };

    const recordDirectionClick = () => {
        publicRequest.post(`/mosques/record-directions/${mosqueID}`, {
            user: user ? { name: user.name, email: user.email, googleId: user.googleId || user.sub, picture: user.picture } : null
        }).catch(() => {});
    };

    const getDirections = () => {
        if (mosque.googleMapsLink) {
            recordDirectionClick();
            window.open(mosque.googleMapsLink, '_blank');
        } else {
            console.error("No map link available");
        }
    };


    const formatDate = (date) => {
        return new Date(date).toLocaleDateString("en-US", {
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const VisitStats = () => (
        visitStats && (
            <div className="mosque-page__stats">
                <div className="stat-card">
                    <h4>Today's Visits</h4>
                    <span>{visitStats.today}</span>
                </div>
                <div className="stat-card">
                    <h4>Monthly Visits</h4>
                    <span>{visitStats.thisMonth}</span>
                </div>
                <div className="stat-card">
                    <h4>Total Visits</h4>
                    <span>{visitStats.total}</span>
                </div>
            </div>
        )
    );
    const handleImageUpload = async () => {
        if (!selectedImage || !user) return;

        const formData = new FormData();
        formData.append('image', selectedImage);
        formData.append('user', JSON.stringify({
            name: user.name,
            email: user.email,
            picture: user.picture,
            googleId: user.sub || user.googleId
        }));

        setUploadLoading(true);
        try {
            // Do NOT set Content-Type - let browser set multipart/form-data with boundary
            const response = await publicRequest.post(`/mosques/upload-image/${mosqueID}`, formData);
            console.log("response", response.data);

            setUploadError(null);
            // Create proper image object structure
            const newImage = {
                url: response.data.imageUrl,
                urlSmall: response.data.imageUrlSmall,
                uploadedBy: {
                    name: user.name,
                    email: user.email,
                    picture: user.picture,
                    googleId: user.sub || user.googleId
                },
                uploadedAt: new Date().toISOString()
            };

            // Update mosque state with proper image structure
            setMosque(prev => ({
                ...prev,
                images: [...(prev.images || []), newImage]
            }));

            setShowImageUpload(false);
            setSelectedImage(null);
            setPreviewUrl(null);
        } catch (error) {
            console.error('Error uploading image:', error);
            const msg = error.response?.data?.message || error.message || 'Failed to upload image';
            setUploadError(msg);
            snackbar.error(msg);
        } finally {
            setUploadLoading(false);
        }
    };

    const handleImageClick = (image) => {
        setSelectedImage(image);
        setImageLoading(true); // Reset loading state
        document.body.style.overflow = 'hidden';
    };

    // Add this function to close the viewer
    const closeImageViewer = () => {
        setSelectedImage(null);
        // Restore scrolling
        document.body.style.overflow = 'auto';
    };

    const handleReportSubmit = async (e) => {
        e?.preventDefault();
        if (!user || !reportCategory || !reportDescription.trim()) return;
        setReportSubmitting(true);
        setUploadError(null);
        try {
            await publicRequest.post(`/mosques/report/${mosqueID}`, {
                user: { name: user.name, email: user.email, googleId: user.googleId || user.sub, picture: user.picture },
                category: reportCategory,
                description: reportDescription.trim(),
                contact: reportContact.trim() || undefined,
            });
            setReportSuccess(true);
            setTimeout(() => {
                setShowReportModal(false);
                setReportSuccess(false);
                setReportCategory('');
                setReportDescription('');
                setReportContact('');
            }, 1500);
        } catch (err) {
            snackbar.error(err.response?.data?.message || 'Failed to submit report');
        } finally {
            setReportSubmitting(false);
        }
    };

    const openReportModal = () => {
        if (!user) return;
        setShowReportModal(true);
        setReportCategory('');
        setReportDescription('');
        setReportContact('');
        setReportSuccess(false);
    };

    return (
        <>

            <Navbar />
            {loading && <MosqueLoader />}
            {mosque && <div className="mosque-page">
                {/* Header Section */}
                <div className="mosque-page__header">
                    <button className="back-button" onClick={() => navigate(-1)}>
                        <ArrowLeft size={20} />
                    </button>
                    <div className="mosque-page__header-actions">
                        {user && (
                            <button className="report-button" onClick={openReportModal} title="Report this mosque">
                                <Flag size={18} />
                                Report
                            </button>
                        )}
                        {user && mosque.contributor && (user.email === mosque.contributor.email || (user.googleId || user.sub) === mosque.contributor.googleId) && (
                            <button className="edit-button" onClick={() => navigate(`/edit-mosque/${mosqueID}`)}>
                                <Edit size={18} />
                                Edit
                            </button>
                        )}
                    </div>
                </div>

                {/* Hero Section */}
                <div className="mosque-page__hero">
                    <div className="mosque-page__hero-content">
                        <div className="mosque-page__title-section">
                            <h1 className="mosque-page__name">{mosque.name}</h1>
                            {mosque.verified && (
                                <div className="mosque-page__verified-badge">
                                    <svg viewBox="0 0 24 24" fill="none" className="verified-icon">
                                        <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    Verified
                                </div>
                            )}
                        </div>
                        <div className="mosque-page__location-wrapper">
                            <p className="mosque-page__location">
                                {mosque.district}, {mosque.state}
                            </p>
                            <div className="mosque-page__map-actions">
                                {/* <MapPin
                                    size={24}
                                    className="mosque-page__map-icon"
                                    onClick={() => window.open(mosque.googleMapsLink, "_blank")}
                                    title="View on map"
                                /> */}
                                <button
                                    className="mosque-page__directions-btn"
                                    onClick={getDirections}
                                >
                                    <Navigation size={18} />
                                    Get Directions
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="mosque-page__content">
                    {/* Status Card */}
                    <div className="mosque-page__status-card">
                        <div className="status-indicator">
                            <span className={`status-dot ${mosque.status === "available" ? "available" : "unavailable"}`} />
                            <div className="status-info">
                                <span className="status-text">
                                    {mosque.status === "available" ? "Food Available" : "Not Available"}
                                </span>
                                {mosque.statusNote && (
                                    <span className="status-note">
                                        {mosque.statusNote}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="time-info">
                            <Clock size={16} />
                            <span>Last updated: {formatDate(mosque.date)}</span>
                        </div>
                    </div>

                    {/* Details Section */}
                    <section className="mosque-page__section">
                        <h2>Today's Meal</h2>
                        <p className="mosque-page__dish">🍛 {getDishDisplayName(mosque.dish)}</p>
                    </section>

                    <section className="mosque-page__section">
                        <h2>About</h2>
                        <p className="mosque-page__description">{mosque.description}</p>
                    </section>

                    {/* Contact Section */}
                    <section className="mosque-page__section">
                        <h2>Contact Information</h2>
                        <div className="contact-info">
                            {mosque.phone && (
                                <a href={`tel:${mosque.phone}`} className="contact-item">
                                    <Phone size={20} />
                                    <span>{mosque.phone}</span>
                                </a>
                            )}
                            {mosque.email && (
                                <a href={`mailto:${mosque.email}`} className="contact-item">
                                    <Mail size={20} />
                                    <span>{mosque.email}</span>
                                </a>
                            )}
                        </div>
                    </section>
                    <section className="mosque-page__section">
                        <h2>Contribution Details</h2>
                        <div className="contributor-info">
                            {mosque.contributor && (
                                <div
                                    className="contributor-card contributor-card--clickable"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => navigate(`/profile/${encodeURIComponent(mosque.contributor.email)}`)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            navigate(`/profile/${encodeURIComponent(mosque.contributor.email)}`);
                                        }
                                    }}
                                >
                                    <div className="contributor-header">
                                        <img
                                            src={mosque.contributor?.picture || DEFAULT_AVATAR}
                                            alt={mosque.contributor?.name}
                                            className="contributor-avatar"
                                            onError={(e) => {
                                                e.target.src = DEFAULT_AVATAR;
                                                e.target.onerror = null;
                                            }}
                                        />
                                        <div className="contributor-details">
                                            <span className="contributor-name">
                                                {mosque.contributor?.name}
                                            </span>
                                            <span className="contributor-date">
                                                <Calendar size={14} />
                                                Added on {formatDate(mosque.contributor?.date)}
                                            </span>
                                        </div>
                                    </div>
                                    {user && mosque.contributor && (user.email === mosque.contributor.email || (user.googleId || user.sub) === mosque.contributor.googleId) && visitStats && (
                                        <div className="contributor-impact">
                                            <p className="contributor-impact__title">Your mosque is helping believers</p>
                                            <div className="contributor-impact__stats">
                                                <div className="contributor-impact__stat">
                                                    <span className="contributor-impact__number">{visitStats.total}</span>
                                                    <span className="contributor-impact__label">Believers viewed</span>
                                                </div>
                                                {visitStats.directionClicks && (
                                                    <div className="contributor-impact__stat">
                                                        <span className="contributor-impact__number">{visitStats.directionClicks.total}</span>
                                                        <span className="contributor-impact__label">Got directions</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </section>
                    {/* <VisitStats /> */}

                    <section className="mosque-page__section">
                        <h2>
                            <Image size={20} className="section-icon" />
                            Photos
                        </h2>
                        {mosque.images && mosque.images.length > 0 ? (
                            <div className="mosque-images-grid">
                                {(mosque.images || []).map((image, index) => (
                                    <div key={index} className="image-card">
                                        <div className="image-card__img-wrap" onClick={() => handleImageClick(image)}>
                                            <img
                                                src={image.urlSmall || image.url}
                                                alt={`${mosque.name} - ${index + 1}`}
                                            />
                                            {isOwnImage(image) && (
                                                <button
                                                    type="button"
                                                    className="image-card__delete-btn"
                                                    onClick={(e) => handleDeleteImage(index, e)}
                                                    disabled={deletingIndex === index}
                                                    title="Delete image"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            )}
                                        </div>
                                        <div className="image-info">
                                            {image.uploadedBy && (
                                                <div className="uploader-info">
                                                    <img
                                                        src={image.uploadedBy.picture || DEFAULT_AVATAR}
                                                        alt={image.uploadedBy.name || 'User'}
                                                        className="uploader-avatar"
                                                        onError={(e) => {
                                                            e.target.src = DEFAULT_AVATAR;
                                                            e.target.onerror = null;
                                                        }}
                                                    />
                                                    <span>{image.uploadedBy.name || 'Anonymous'}</span>
                                                </div>
                                            )}
                                            <span className="upload-date">
                                                {image.uploadedAt ?
                                                    new Date(image.uploadedAt).toLocaleDateString() :
                                                    new Date().toLocaleDateString()
                                                }
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="no-images">
                                <Image size={40} />
                                <p>No images uploaded yet</p>
                                {user && (
                                    <button
                                        className="upload-prompt-btn"
                                        onClick={() => setShowImageUpload(true)}
                                    >
                                        Upload First Image
                                    </button>
                                )}
                            </div>
                        )}
                    </section>

                    {/* Add Image Viewer Overlay */}
                    {selectedImage && (
                        <div className="image-viewer-overlay" onClick={closeImageViewer}>
                            <div className="image-viewer-content" onClick={e => e.stopPropagation()}>
                                <button className="close-viewer" onClick={closeImageViewer}>
                                    ×
                                </button>

                                {/* Skeleton loader */}
                                {imageLoading && (
                                    <div className="image-skeleton">
                                        <div className="skeleton-pulse"></div>
                                    </div>
                                )}

                                <img
                                    src={selectedImage.url}
                                    alt="Full size"
                                    className={`full-size-image ${imageLoading ? 'hidden' : ''}`}
                                    onLoad={() => setImageLoading(false)}
                                />

                                <div className="image-viewer-info">
                                    <div className="uploader-info">
                                        <img
                                            src={selectedImage.uploadedBy?.picture || DEFAULT_AVATAR}
                                            alt={selectedImage.uploadedBy?.name}
                                            onError={(e) => {
                                                e.target.src = DEFAULT_AVATAR;
                                                e.target.onerror = null;
                                            }}
                                            className="uploader-avatar"
                                        />
                                        <span>{selectedImage.uploadedBy?.name}</span>
                                    </div>
                                    <span className="upload-date">
                                        {new Date(selectedImage.uploadedAt).toLocaleDateString()}
                                    </span>
                                    {isOwnImage(selectedImage) && (
                                        <button
                                            type="button"
                                            className="image-viewer__delete-btn"
                                            onClick={() => {
                                                const idx = mosque.images?.findIndex(img => img.url === selectedImage.url);
                                                if (idx >= 0) handleDeleteImage(idx, null, closeImageViewer);
                                            }}
                                            disabled={deletingIndex !== null}
                                        >
                                            <Trash2 size={18} /> Delete
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Comments Section */}
                    <section className="mosque-page__section comments-section">
                        <h2>Comments</h2>

                        {user ? (
                            <form className="comment-form" onSubmit={handleCommentSubmit} ref={commentsRef} id="comments">
                                <div className="comment-input-wrapper">
                                    <img
                                        src={user.picture}
                                        alt={user.name}
                                        className="comment-user-avatar"
                                    />
                                    <textarea
                                        value={newComment}
                                        onChange={(e) => setNewComment(e.target.value)}
                                        placeholder="Add a comment..."
                                        className="comment-input"
                                        rows="2"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="comment-submit-btn"
                                    disabled={!newComment.trim()}
                                >
                                    Post Comment
                                </button>
                            </form>
                        ) : (
                            <div className="login-prompt">
                                Please <Link to="/login">login</Link> to add comments
                            </div>
                        )}

                        <div className="comments-list">
                            {mosque.comments && [...mosque.comments]
                                .sort((a, b) => new Date(b.date) - new Date(a.date)) // Sort comments by date
                                .map((comment) => (
                                    <div key={comment._id} className="comment-card">
                                        <div className="comment-header">
                                            <img
                                                src={comment.picture}
                                                alt={comment.name}
                                                className="comment-avatar"
                                            />
                                            <div className="comment-meta">
                                                <span className="comment-author">
                                                    {comment.name}
                                                </span>
                                                <span className="comment-date">
                                                    {formatDate(comment.date)}
                                                </span>
                                            </div>
                                            {isOwnComment(comment) && (
                                                <button
                                                    type="button"
                                                    className="comment-delete-btn"
                                                    onClick={(e) => handleDeleteComment(comment._id, e)}
                                                    disabled={deletingCommentId === comment._id}
                                                    title="Delete comment"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            )}
                                        </div>
                                        <p className="comment-content">{comment.content}</p>
                                    </div>
                                ))}
                        </div>
                    </section>
                </div>
            </div>}
            {user && (
                <button
                    className="floating-add-image"
                    onClick={() => setShowImageUpload(true)}
                >
                    <ImagePlus size={24} />
                    Add Image
                </button>
            )}
            {showImageUpload && (
                <div className="image-upload-overlay">
                    <div className="overlay-background" onClick={() => { setShowImageUpload(false); setUploadError(null); }} />
                    <div className="upload-modal">
                        <h2>Upload Mosque Image</h2>
                        <div className="upload-area">
                            {selectedImage ? (
                                <div className="image-preview">
                                    <img
                                        src={URL.createObjectURL(selectedImage)}
                                        alt="Preview"
                                    />
                                    <button
                                        className="remove-image"
                                        onClick={() => setSelectedImage(null)}
                                    >
                                        Remove
                                    </button>
                                </div>
                            ) : (
                                <label className="image-input-label">
                                    <ImagePlus size={48} />
                                    <span>Click to select image</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => setSelectedImage(e.target.files[0])}
                                        hidden
                                    />
                                </label>
                            )}
                        </div>
                        {uploadError && (
                            <div className="upload-error">
                                {uploadError}
                            </div>
                        )}
                        <div className="modal-actions">
                            <button
                                className="cancel-button"
                                onClick={() => { setShowImageUpload(false); setUploadError(null); }}
                            >
                                Cancel
                            </button>
                            <button
                                className="upload-button"
                                onClick={handleImageUpload}
                                disabled={!selectedImage || uploadLoading}
                            >
                                {uploadLoading ? 'Uploading...' : 'Upload Image'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showReportModal && user && (
                <div className="delete-confirm-overlay" onClick={() => !reportSubmitting && setShowReportModal(false)}>
                    <div className="report-modal" onClick={e => e.stopPropagation()}>
                        <div className="report-modal-header">
                            <h3>Report Mosque</h3>
                            <button className="report-modal-close" onClick={() => !reportSubmitting && setShowReportModal(false)}>×</button>
                        </div>
                        {reportSuccess ? (
                            <div className="report-success">Thank you! Your report has been submitted.</div>
                        ) : (
                            <form className="report-form" onSubmit={handleReportSubmit}>
                                <label>
                                    Category
                                    <select value={reportCategory} onChange={(e) => setReportCategory(e.target.value)} required>
                                        <option value="">Select a category</option>
                                        <option value="wrong_info">Wrong information</option>
                                        <option value="closed">Mosque closed</option>
                                        <option value="inappropriate_content">Inappropriate content</option>
                                        <option value="other">Other</option>
                                    </select>
                                </label>
                                <label>
                                    Description *
                                    <textarea placeholder="Describe the issue..." value={reportDescription} onChange={(e) => setReportDescription(e.target.value)} required rows={4} />
                                </label>
                                <label>
                                    Contact (optional)
                                    <input type="text" placeholder="Phone or email for follow-up" value={reportContact} onChange={(e) => setReportContact(e.target.value)} />
                                </label>
                                <div className="report-modal-actions">
                                    <button type="button" className="cancel-button" onClick={() => setShowReportModal(false)} disabled={reportSubmitting}>Cancel</button>
                                    <button type="submit" className="report-submit-btn" disabled={reportSubmitting || !reportCategory || !reportDescription.trim()}>
                                        {reportSubmitting ? 'Submitting...' : 'Submit Report'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
            {commentDeleteConfirmId && (
                <div className="delete-confirm-overlay" onClick={() => setCommentDeleteConfirmId(null)}>
                    <div className="delete-confirm-popup" onClick={e => e.stopPropagation()}>
                        <div className="delete-confirm-header">
                            <div className="delete-confirm-icon">
                                <AlertTriangle size={24} />
                            </div>
                            <h3>Delete Comment</h3>
                        </div>
                        <p className="delete-confirm-message">Are you sure you want to delete this comment? This cannot be undone.</p>
                        <div className="delete-confirm-actions">
                            <button className="delete-confirm-btn cancel" onClick={() => setCommentDeleteConfirmId(null)}>
                                Cancel
                            </button>
                            <button className="delete-confirm-btn confirm" onClick={confirmDeleteComment} disabled={deletingCommentId !== null}>
                                {deletingCommentId ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {deleteConfirmIndex !== null && (
                <div className="delete-confirm-overlay" onClick={() => setDeleteConfirmIndex(null)}>
                    <div className="delete-confirm-popup" onClick={e => e.stopPropagation()}>
                        <div className="delete-confirm-header">
                            <div className="delete-confirm-icon">
                                <AlertTriangle size={24} />
                            </div>
                            <h3>Delete Image</h3>
                        </div>
                        <p className="delete-confirm-message">Are you sure you want to delete this image? This cannot be undone.</p>
                        <div className="delete-confirm-actions">
                            <button className="delete-confirm-btn cancel" onClick={() => setDeleteConfirmIndex(null)}>
                                Cancel
                            </button>
                            <button className="delete-confirm-btn confirm" onClick={confirmDeleteImage} disabled={deletingIndex !== null}>
                                {deletingIndex === deleteConfirmIndex ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            </>
    );
};

export default MosquePage;